import { Close } from '@mui/icons-material'
import { Box, Button, Card, CardContent, CardHeader, CircularProgress, IconButton, MenuItem, Select, TextField } from '@mui/material'
import { useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { ALL_REPORT_PAGE_ORIENTATION, ALL_REPORT_PAGE_SIZE, ReportPageOrientation, ReportPageSize } from '../../definitions/report_builder/ReportBuilderDefinitions'
import { useAppDispatch } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import { buildQueryKey } from '../hooks/useQuery'
import { snackbar } from '../services/SnackbarService'

interface P {
    onClose?: () => void
}
export default function ModalCreateReport({ onClose }: P) {
    const dispatch = useAppDispatch()
    const [name, setName] = useState('New Report')
    const [pageOrientation, setPageOrientation] = useState<ReportPageOrientation>('portrait')
    const [pageSize, setPageSize] = useState<ReportPageSize>('A4')
    const invalidate = useInvalidate({ queryKey: buildQueryKey('reports') })
    const isFormValid = name.trim().length >= 3
    const invalidateApprovals = useInvalidate({ queryKey: buildQueryKey('get-approvals') })

    const { execute, status } = useCommand({
        commandFn: () => {
            return ApiClient.createReport(name, pageSize, pageOrientation)
        },
        onFinish: result => {
            if (result.status === 'success') {
                // console.log('CREATING REPORT: ', result.response)
                invalidate()
                invalidateApprovals()
                dispatch(changeRoute({ route: 'report-editor', params: { report: result.response } }))
            } else {
                snackbar.show({ severity: 'error', text: 'Error creating report template' })
            }
        }
    })

    const previewPdf = () => {
        const sizesInInches: Record<ReportPageSize, [number, number]> = {
            A4: [8.27, 11.69],
            A3: [11.69, 16.54]
        }

        const [wIn, hIn] = sizesInInches[pageSize]

        const width = pageOrientation === 'landscape' ? hIn : wIn
        const height = pageOrientation === 'landscape' ? wIn : hIn

        const scale = Math.min(width, height, 1)
        const previewWidth = width * scale * 11
        const previewHeight = height * scale * 11

        return (
            <Box display='flex' alignItems='center' justifyContent='center' border={1} borderColor='divider' borderRadius={2} bgcolor='background.paper' height={200} width='100%' marginTop={2} paddingY={3}>
                <Box textAlign='center' width='100%'>
                    <Box
                        width={previewWidth}
                        height={previewHeight}
                        bgcolor='background.default'
                        border={1}
                        borderColor='divider'
                        boxShadow={1}
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                        fontSize={18}
                        color='text.secondary'
                        sx={{ transition: 'width 0.3s, height 0.3s', margin: '0 auto' }}>
                        {pageSize}
                        <br />
                        {pageOrientation}
                    </Box>
                    <Box mt={1} color='text.secondary' fontSize={14}>
                        {`${width.toFixed(2)} × ${height.toFixed(2)} in`}
                    </Box>
                </Box>
            </Box>
        )
    }

    return (
        <Box margin='80px auto auto' maxWidth={540}>
            <Card>
                <CardHeader

                    title='Create new Report'
                    action={(
                        <IconButton onClick={onClose} size='small'>
                            <Close />
                        </IconButton>
                    )}
                />
                <CardContent>
                    <Box display='flex' flexDirection='column' gap={2}>
                        <TextField
                            size='small'
                            fullWidth
                            value={name}
                            InputLabelProps={{ shrink: true }}
                            required
                            inputProps={{ minLength: 3 }}
                            helperText='Minimum 3 characters'
                            onChange={e => setName(e.target.value)}
                        />
                        <Select
                            size='small'
                            value={pageOrientation}
                            fullWidth
                            onChange={e => setPageOrientation(e.target.value as ReportPageOrientation)}>
                            {ALL_REPORT_PAGE_ORIENTATION.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </Select>
                        <Select
                            size='small'
                            value={pageSize}
                            fullWidth
                            onChange={e => setPageSize(e.target.value as ReportPageSize)}>
                            {ALL_REPORT_PAGE_SIZE.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </Select>
                        {previewPdf()}
                        <Box display='flex' justifyContent='flex-end' marginTop={3}>
                            <Button variant='contained' onClick={execute} disabled={!isFormValid || status === 'loading'}>
                                {status === 'loading' && <CircularProgress size={20} color='secondary' sx={{ marginRight: 1 }} />}
                                Create Report
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}
